import { CommonModule } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core'
import { SkeletonModule } from 'primeng/skeleton'
import {
  AllSalesCellSelection,
  AllSalesColumn,
  AllSalesMerchantTable,
  AllSalesTableLayout,
  DEFAULT_ALL_SALES_METRIC_LABELS,
  DEFAULT_ALL_SALES_TABLE_LAYOUT,
  SalesMetricKey,
  SalesMetrics,
} from './all-sales-table.model'

@Component({
  selector: 'app-all-sales-table',
  standalone: true,
  imports: [CommonModule, SkeletonModule],
  templateUrl: './all-sales-table.component.html',
  styleUrl: './all-sales-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AllSalesTableComponent implements AfterViewInit, OnChanges {
  @Input({ required: true }) merchantTables: AllSalesMerchantTable[] = []
  @Input() loading = false
  @Input() layout: AllSalesTableLayout = DEFAULT_ALL_SALES_TABLE_LAYOUT
  @Input() metricLabels: Record<SalesMetricKey, string> = DEFAULT_ALL_SALES_METRIC_LABELS
  @Input() selectedCell: AllSalesCellSelection | null = null
  @Output() cellSelected = new EventEmitter<AllSalesCellSelection>()
  @ViewChildren('scrollContainer') private scrollContainers!: QueryList<ElementRef<HTMLElement>>

  readonly metricKeys: SalesMetricKey[] = ['amount', 'cardTransactions', 'count']
  private readonly monthFormatter = new Intl.DateTimeFormat('en-GB', {
    month: 'long',
    year: 'numeric',
  })
  private readonly countFormatter = new Intl.NumberFormat('en-GB', {
    maximumFractionDigits: 0,
  })
  private readonly currencyFormatter = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  private readonly formattedMonths = new Map<string, string>()
  hoveredMerchantName: string | null = null
  hoveredMonth: string | null = null
  private scrollToEndPending = false

  ngAfterViewInit(): void {
    this.scrollContainers.changes.subscribe(() => this.queueScrollToEnd())
    this.queueScrollToEnd()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['merchantTables']) this.queueScrollToEnd()
  }

  gridColumns(merchantTable: AllSalesMerchantTable): string {
    const terminalCount = merchantTable.cashAdvances.reduce(
      (count, cashAdvance) => count + cashAdvance.columns.length,
      0,
    )
    return `${this.layout.merchantWidth} ${this.layout.monthWidth} ${this.layout.metricWidth} repeat(${terminalCount}, ${this.layout.valueWidth}) ${this.layout.valueWidth}`
  }

  get skeletonGridColumns(): string {
    return `${this.layout.merchantWidth} ${this.layout.monthWidth} ${this.layout.metricWidth} repeat(3, ${this.layout.valueWidth}) ${this.layout.valueWidth}`
  }

  formatMonth(month: string): string {
    const cached = this.formattedMonths.get(month)
    if (cached) return cached
    const [year, monthNumber] = month.split('-').map(Number)
    const formatted = this.monthFormatter.format(
      new Date(year, monthNumber - 1, 1),
    )
    this.formattedMonths.set(month, formatted)
    return formatted
  }

  formatMetric(metrics: SalesMetrics | undefined, metric: SalesMetricKey): string {
    if ((metrics?.count ?? 0) === 0) return ''

    const value = metrics?.[metric] ?? 0
    return metric === 'count'
      ? this.countFormatter.format(value)
      : this.currencyFormatter.format(value)
  }

  selectCell(merchantTable: AllSalesMerchantTable, month: string, column?: AllSalesColumn): void {
    this.cellSelected.emit({
      merchantId: merchantTable.merchantId,
      cashAdvanceId: column?.cashAdvanceId,
      terminalId: column?.terminalId,
      filterByCashAdvance: Boolean(column),
      filterByTerminal: Boolean(column),
      month,
      terminalName: column?.terminalName ?? 'Total',
    })
  }

  isSelectedCell(merchantTable: AllSalesMerchantTable, month: string, column?: AllSalesColumn): boolean {
    return this.selectedCell?.merchantId === merchantTable.merchantId
      && this.selectedCell.month === month
      && this.selectedCell.cashAdvanceId === column?.cashAdvanceId
      && this.selectedCell.terminalId === column?.terminalId
  }

  setHoveredRow(merchantTable: AllSalesMerchantTable, month: string | null): void {
    const merchantName = month ? merchantTable.merchantName : null
    if (this.hoveredMerchantName === merchantName && this.hoveredMonth === month) {
      return
    }
    this.hoveredMerchantName = merchantName
    this.hoveredMonth = month
  }

  isHoveredRow(merchantTable: AllSalesMerchantTable, month: string): boolean {
    return this.hoveredMerchantName === merchantTable.merchantName && this.hoveredMonth === month
  }

  private queueScrollToEnd(): void {
    if (this.scrollToEndPending) return

    this.scrollToEndPending = true
    requestAnimationFrame(() => requestAnimationFrame(() => {
      this.scrollContainers?.forEach(({ nativeElement }) => {
        nativeElement.scrollLeft = nativeElement.scrollWidth
      })
      this.scrollToEndPending = false
    }))
  }
}
